import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import './styles/LoadingLine.css';

const TICK_MS = 130;
const HOLD_PCT = 88;
const COMPLETE_HIDE_MS = 420;
const START_PCT = 4;

/**
 * Top loading bar driven by the {@link busy} prop.
 *
 * Guarantees:
 * - Width never decreases while the bar is visible.
 * - When busy → false, width jumps to 100% once and the bar hides shortly after.
 * - A new busy session resets the bar without animating backward (snap, no transition).
 */
export default function LoadingLine({ busy }) {
    const [shown, setShown] = useState(false);
    const [widthPct, setWidthPct] = useState(0);
    const [noTransition, setNoTransition] = useState(false);

    const shownRef = useRef(false);
    const intervalRef = useRef(null);
    const hideTimerRef = useRef(null);
    const rafRef = useRef(null);

    useEffect(() => {
        const clearInterval_ = () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
        const clearHideTimer = () => {
            if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current);
                hideTimerRef.current = null;
            }
        };
        const cancelRaf = () => {
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };

        if (busy) {
            clearHideTimer();

            // Snap reset (no animation) only on a new session,
            // so the bar never animates backwards.
            setNoTransition(true);
            setWidthPct(START_PCT);
            cancelRaf();
            rafRef.current = requestAnimationFrame(() => {
                setNoTransition(false);
                rafRef.current = null;
            });

            shownRef.current = true;
            setShown(true);

            clearInterval_();
            intervalRef.current = setInterval(() => {
                setWidthPct((w) => {
                    if (w >= HOLD_PCT) {
                        return w; // never decrease, never overshoot the hold
                    }
                    const step = Math.max(0.6, (90 - w) * 0.065);
                    return Math.min(HOLD_PCT, w + step);
                });
            }, TICK_MS);

            return () => {
                clearInterval_();
                cancelRaf();
            };
        }

        clearInterval_();

        if (!shownRef.current) {
            return undefined;
        }

        setNoTransition(false);
        setWidthPct(100);

        clearHideTimer();
        hideTimerRef.current = setTimeout(() => {
            shownRef.current = false;
            setShown(false);
            setWidthPct(0);
            hideTimerRef.current = null;
        }, COMPLETE_HIDE_MS);

        return () => {
            clearHideTimer();
        };
    }, [busy]);

    if (!shown) {
        return null;
    }

    const finishing = widthPct >= 100;
    const transitionStyle = noTransition
        ? 'none'
        : finishing
          ? 'width 0.32s ease-out'
          : 'width 0.2s ease-out';

    return (
        <div className="loading-line-container">
            <div
                className="loading-line"
                style={{
                    width: `${widthPct}%`,
                    transition: transitionStyle,
                }}
            />
        </div>
    );
}

LoadingLine.propTypes = {
    busy: PropTypes.bool.isRequired,
};
